import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, ref, watch } from 'vue';
import { UpdateCompanySdsesRequestBody } from '@/interfaces/api/general/company';
import { Department } from '@/interfaces/model/general/department';
import { Sds } from '@/interfaces/model/general/sds';
import { TagResponse } from '@/interfaces/model/general/tag';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { useTagComposable } from '@/composables/useTag';
import BaseFormCard from '@/views/base/BaseFormCard.vue';
import { usePermissionComposable } from '@/composables/usePermission';
import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import SelectTags from '@/components/SelectTags.vue';
import TagChip from '@/components/TagChip.vue';
import { defineMessages } from '@/lib/i18n';
import { i18n as i18nModule } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'BulkUpdateDialog',
  props: {
    value: { type: Boolean },
    selectedSdses: null,
    selectedSdsCount: null
  },
  emits: ["input", "update"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (
    e: 'update',
    form: Omit<UpdateCompanySdsesRequestBody, 'sds_uuid_list'>
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  selectedSdses: Sds[];
  selectedSdsCount: number;
};

const $t = i18nModule.t.bind(i18nModule);

const m = defineMessages({
  addConfirmationTitle: '選択した項目を一括追加しますか？',
  addInstructions:
    '追加したい項目を入力し、「追加する」ボタンを押してください。',
  addConfirmation:
    '「実行する」をクリックすると、選択された{count}件のSDSファイルに以下の項目が追加されます。',
  deleteInstructions:
    '削除したい項目を選択し、「削除する」ボタンを押してください。',
  deleteConfirmationTitle: '選択した項目を一括削除しますか？',
  deleteConfirmation:
    '「実行する」をクリックすると、選択された{count}件のSDSファイルから以下の項目が削除されます。この操作は取り消すことはできません。',
  noDeletableDepartment: '削除可能な部署がありません。',
  noDeletableTag: '削除可能なタグがありません。',
});

const {
  companyDepartments,
  getCompanyDepartments,
  departmentTree,
  getCompanyDepartmentTree,
  editableDepartmentTree,
} = useDepartmentComposable();
const { tags, getCompanyTag } = useTagComposable();
const {
  hasPermission,
  hasSdsEditPermission,
  PERMISSIONS: P,
} = usePermissionComposable();




const dialogValue = ref(props.value);

watch(
  () => props.value,
  (newValue) => {
    dialogValue.value = newValue;
  }
);

watch(dialogValue, (newValue) => {
  emit('input', newValue);
});

const selectedDepartments = ref<Department[]>([]);
const selectedTags = ref<TagResponse[]>([]);
const updateSelectedTagIds = ref<number[]>([]);

const activeTab = ref<number>();
const isDeleteMode = computed(() => {
  return activeTab.value === 1;
});
const currentStep = ref<'select' | 'confirm'>('select');

const isSelected = computed(() => {
  const hasSelection =
    selectedDepartments.value.length > 0 || selectedTags.value.length > 0;
  return hasSelection && !hasUnauthorizedSelection.value;
});
const existingDepartments = computed(() => {
  const hashMap = new Map<string, Department>();
  props.selectedSdses.forEach((sds) => {
    sds.departments.forEach((d) => {
      if (!companyDepartments.value.find((cd) => cd.uuid === d.uuid)) {
        return;
      }
      hashMap.set(d.uuid, d);
    });
  });
  return Array.from(hashMap.values());
});
const existingTags = computed(() => {
  const hashMap = new Map<number, TagResponse>();
  props.selectedSdses.forEach((sds) => {
    sds.tags.forEach((t) => {
      hashMap.set(t.id, t);
    });
  });
  return Array.from(hashMap.values());
});

const updateSelectDepartments = (updateValues: string[]) => {
  const formattedSelectDepartment = updateValues
    .map((d) => companyDepartments.value.find((cd) => cd.uuid === d))
    .filter((d) => !!d);
  selectedDepartments.value = formattedSelectDepartment;
};
const updateSelectTags = (updateValues: number[]) => {
  const formattedSelectTags = updateValues
    .map((t) => tags.value.find((tag) => tag.id === t))
    .filter((t) => !!t);
  selectedTags.value = formattedSelectTags;
  updateSelectedTagIds.value = updateValues;
};

const closeDialog = () => {
  activeTab.value = 0;
  selectedDepartments.value = [];
  selectedTags.value = [];
  updateSelectedTagIds.value = [];
  currentStep.value = 'select';
  dialogValue.value = false;
};

const handleTabChange = () => {
  selectedDepartments.value = [];
  selectedTags.value = [];
  updateSelectedTagIds.value = [];
  currentStep.value = 'select';
};
const handleNextStepClick = () => {
  if (hasUnauthorizedSelection.value) {
    return;
  }
  if (activeTab.value === 0) {
    updateSelectedTagIds.value = selectedTags.value.map((t) => t.id);
  }
  currentStep.value = 'confirm';
};
const handleBackStepClick = () => {
  currentStep.value = 'select';
};
const handleExecuteClick = () => {
  if (isDeleteMode.value) {
    emit('update', {
      delete_department_id_list: selectedDepartments.value.map((d) => d.uuid),
      delete_tag_id_list:
        updateSelectedTagIds.value.length > 0
          ? updateSelectedTagIds.value
          : selectedTags.value.map((t) => t.id),
    });
  } else {
    emit('update', {
      add_department_id_list: selectedDepartments.value.map((d) => d.uuid),
      add_tag_id_list:
        updateSelectedTagIds.value.length > 0
          ? updateSelectedTagIds.value
          : selectedTags.value.map((t) => t.id),
    });
  }
  closeDialog();
};
const handleCancelClick = () => {
  closeDialog();
};

const toggleDepartment = (department: Department) => {
  if (selectedDepartments.value.find((d) => d.uuid === department.uuid)) {
    removeDepartment(department.uuid);
  } else {
    selectedDepartments.value.push(department);
  }
};
const removeDepartment = (uuid?: string) => {
  if (!uuid) return;
  selectedDepartments.value = selectedDepartments.value.filter(
    (department: Department) => department.uuid !== uuid
  );
};
const toggleTag = (tag: TagResponse) => {
  if (selectedTags.value.find((t) => t.id === tag.id)) {
    removeTag(tag.id);
  } else {
    selectedTags.value.push(tag);
    if (!updateSelectedTagIds.value.includes(tag.id)) {
      updateSelectedTagIds.value.push(tag.id);
    }
  }
};
const removeTag = (id: number) => {
  selectedTags.value = selectedTags.value.filter(
    (tag: TagResponse) => tag.id !== id
  );
  updateSelectedTagIds.value = updateSelectedTagIds.value.filter(
    (tagId) => tagId !== id
  );
};

const isSelectedDepartment = (uuid: string) => {
  return selectedDepartments.value.some((d) => d.uuid === uuid);
};
const isSelectedTag = (id: number) => {
  return selectedTags.value.some((t) => t.id === id);
};
const dialogTitle = computed(() => {
  if (currentStep.value === 'select') {
    return $t(sharedMessages.bulkEdit);
  }
  return $t(
    isDeleteMode.value ? m.deleteConfirmationTitle : m.addConfirmationTitle
  );
});

const handleDepartmentItemDisabled = (item: Department) => {
  return !hasSdsEditPermission([item]);
};

const selectableDepartmentTree = computed(() => {
  return hasPermission(P.SDS_EDIT_OTHER_DEPARTMENT)
    ? departmentTree.value
    : editableDepartmentTree.value;
});

const unauthorizedSdses = computed(() => {
  return props.selectedSdses.filter(
    (sds) => !hasSdsEditPermission(sds.departments)
  );
});

const hasUnauthorizedSelection = computed(() => {
  return unauthorizedSdses.value.length > 0;
});

onBeforeMount(async () => {
  await getCompanyDepartments();
  await getCompanyDepartmentTree();
  await getCompanyTag();
});

return { __sfc: true,$t, m, companyDepartments, getCompanyDepartments, departmentTree, getCompanyDepartmentTree, editableDepartmentTree, tags, getCompanyTag, hasPermission, hasSdsEditPermission, P, props, emit, dialogValue, selectedDepartments, selectedTags, updateSelectedTagIds, activeTab, isDeleteMode, currentStep, isSelected, existingDepartments, existingTags, updateSelectDepartments, updateSelectTags, closeDialog, handleTabChange, handleNextStepClick, handleBackStepClick, handleExecuteClick, handleCancelClick, toggleDepartment, removeDepartment, toggleTag, removeTag, isSelectedDepartment, isSelectedTag, dialogTitle, handleDepartmentItemDisabled, selectableDepartmentTree, unauthorizedSdses, hasUnauthorizedSelection, BaseFormCard, DepartmentTreeSelect, SelectTags, TagChip, sharedMessages }
}

})